import { createContext, useEffect, useCallback, useContext, useReducer, useState } from 'react';

import decode from 'jwt-decode';
import { omit } from 'lodash';
import { rawAPI } from '../services/api';
import ToastContext from './toast-context';
import config from '../config';

const AuthContext = createContext({
  token: '',
  user: undefined,
  isLoggedIn: false,
  isAdmin: false,
  isSuperUser: false,
  logoutNotified: false,
  login: () => {},
  logout: () => {},
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();
  const remainingDuration = adjExpirationTime - currentTime;
  return remainingDuration;
};

const retrieveRemainingSessionTime = () => {
  const storedExpirationDate = localStorage.getItem(`${config.appName}_expirationTime`);

  const remainingTime = calculateRemainingTime(storedExpirationDate);

  if (remainingTime <= 30000) {
    localStorage.removeItem(`${config.appName}_expirationTime`);
    localStorage.removeItem(`${config.appName}_user`);
    return null;
  }

  return remainingTime;
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'login': {
      const decoded = decode(action.payload.token);
      const expirationTime = new Date(decoded.exp * 1000);
      localStorage.setItem(`${config.appName}_expirationTime`, expirationTime);
      localStorage.setItem(`${config.appName}_user`, JSON.stringify(decoded));
      return {
        ...state,
        user: omit(decoded, 'iat', 'exp'),
        remainingTime: calculateRemainingTime(expirationTime),
      };
    }
    case 'refresh': {
      const expirationTime = localStorage.getItem(`${config.appName}_expirationTime`);
      const localuser = JSON.parse(localStorage.getItem(`${config.appName}_user`));
      return {
        ...state,
        user: localuser,
        remainingTime: calculateRemainingTime(expirationTime),
      };
    }
    case 'logout':
    case 'clear-token':
      localStorage.removeItem(`${config.appName}_expirationTime`);
      localStorage.removeItem(`${config.appName}_user`);
      return { ...state, token: '', user: null, remainingTime: null, logoutNotified: true };

    case 'clear-logout-notified':
      return { ...state, logoutNotified: false };
    default:
      console.errog('authReducer() function in auth-context.js reached the default case!');
  }
};

export const AuthProvider = (props) => {
  const { handleError, success, error } = useContext(ToastContext);
  const [initialized, setInitialized] = useState(false);
  const [loginState, dispatch] = useReducer(authReducer, {
    token: '',
    user: null,
    remainingTime: retrieveRemainingSessionTime(),
    logoutNotified: false,
  });

  const logoutHandler = useCallback(
    (sessionExpired = false) => {
      const logout = async () => {
        try {
          await rawAPI.post('/users/logout');
          dispatch({ type: 'logout' });
          if (sessionExpired === true) {
            error('Session expired. Please log in.', {
              onOpen: () => {
                dispatch({ type: 'clear-logout-notified' });
              },
            });
          } else {
            success('Logged out!', {
              onOpen: () => {
                dispatch({ type: 'clear-logout-notified' });
              },
            });
          }
        } catch (err) {
          handleError(err);
        }
      };

      logout();
    },
    [handleError, success, error]
  );

  const loginHandler = useCallback((token) => {
    dispatch({ type: 'login', payload: { token } });
  }, []);

  const refreshHandler = useCallback(() => {
    dispatch({ type: 'refresh', payload: {} });
  }, []);

  const clearToken = useCallback(() => {
    dispatch({ type: 'clear-token' });
  }, []);

  // Auto-logout timer
  useEffect(() => {
    if (loginState.remainingTime) {
      const logoutTimer = setTimeout(logoutHandler, loginState.remainingTime, true);
      return () => {
        clearTimeout(logoutTimer);
      };
    }
  }, [loginState.remainingTime, logoutHandler]);

  // Access-token initialization
  useEffect(() => {
    const initSession = async () => {
      if (localStorage.getItem(`${config.appName}_user`) != null) {
        refreshHandler();
      }
      setInitialized(true);
    };

    if (!initialized) {
      initSession();
    }
    // Only want this effect to trigger on initial render, leaving deps array blank.
    // failing rule is react-hooks/exhaustive-deps
    // eslint-disable-next-line
  }, []);

  const contextValue = {
    user: loginState.user,
    isLoggedIn: !!loginState.user && loginState.remainingTime > 0,
    isAdmin: loginState.user?.role === 'ADMIN' ? true : false,
    logoutNotified: loginState.logoutNotified,
    login: loginHandler,
    logout: logoutHandler,
    // Should only be called to clear an invalid access token once the session token has expired
    clearToken,
  };

  return initialized ? (
    <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>
  ) : null;
};

export default AuthContext;
